/**
 * ChatBot Debug Logger
 *
 * A clean, modern console logging system for the ChatBot component
 * with proper categorization and visual hierarchy.
 */

// Color scheme for different log categories
const colors = {
  primary: '#3B82F6', // Blue
  success: '#10B981', // Green
  warning: '#F59E0B', // Amber
  error: '#EF4444', // Red
  info: '#6B7280', // Gray
  subscription: '#8B5CF6', // Purple
  message: '#06B6D4', // Cyan
  mutation: '#F97316', // Orange
  auth: '#EC4899' // Pink
} as const

// Log level configuration
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
} as const

// Current log level (can be changed for production)
const CURRENT_LOG_LEVEL = LOG_LEVELS.DEBUG

interface LogContext {
  conversationId?: string
  caseId?: number | string
  messageIndex?: number
  timestamp?: string
  // Allow additional serializable context entries
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | Record<string, unknown>
}

class ChatBotDebugLogger {
  private logGroup(title: string, color: string, expanded = false) {
    const style = `
      color: ${color}; 
      font-weight: bold; 
      font-size: 12px;
      padding: 2px 6px;
      border-radius: 3px;
      background: ${color}15;
    `

    if (expanded) {
      console.group(`%c${title}`, style)
    } else {
      console.groupCollapsed(`%c${title}`, style)
    }
  }

  private formatContext(context?: LogContext) {
    if (!context || Object.keys(context).length === 0) return ''

    const formatted = Object.entries(context)
      .map(([key, value]) => {
        if (typeof value === 'string' && value.length > 50) {
          return `${key}: "${value.substring(0, 47)}..."`
        }
        return `${key}: ${JSON.stringify(value)}`
      })
      .join(', ')

    return `{ ${formatted} }`
  }

  private shouldUseTable(context?: LogContext): boolean {
    if (!context) return false

    // Use table for objects with multiple properties that would benefit from tabular display
    const keys = Object.keys(context)
    return (
      keys.length >= 3 &&
      keys.some(
        (key) =>
          typeof context[key] === 'object' ||
          typeof context[key] === 'number' ||
          typeof context[key] === 'boolean'
      )
    )
  }

  private formatAsTable(context: LogContext) {
    // Convert context to table format
    const tableData = Object.entries(context).map(([key, value]) => ({
      Property: key,
      Value: typeof value === 'object' ? JSON.stringify(value) : String(value),
      Type: typeof value
    }))

    console.table(tableData)
  }

  // 🔐 Authentication & Setup
  auth(message: string, context?: LogContext) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.INFO) return

    this.logGroup('🔐 AUTHENTICATION', colors.auth)
    console.log(`%c${message}`, `color: ${colors.auth}`)
    if (context) {
      if (this.shouldUseTable(context)) {
        this.formatAsTable(context)
      } else {
        console.log(this.formatContext(context))
      }
    }
    console.groupEnd()
  }

  // 🎯 Subscription Management
  subscription(
    action: 'start' | 'connect' | 'data' | 'error' | 'complete',
    message: string,
    context?: LogContext
  ) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.INFO) return

    const icons = {
      start: '🎯',
      connect: '🔌',
      data: '📡',
      error: '❌',
      complete: '🏁'
    }

    this.logGroup(
      `${icons[action]} SUBSCRIPTION ${action.toUpperCase()}`,
      colors.subscription
    )
    console.log(`%c${message}`, `color: ${colors.subscription}`)
    if (context) {
      if (this.shouldUseTable(context)) {
        this.formatAsTable(context)
      } else {
        console.log(this.formatContext(context))
      }
    }
    console.groupEnd()
  }

  // 📤 Mutations & Requests
  mutation(
    action: 'send' | 'success' | 'error',
    message: string,
    context?: LogContext
  ) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.INFO) return

    const icons = { send: '📤', success: '✅', error: '❌' }
    const actionColor = action === 'error' ? colors.error : colors.mutation

    this.logGroup(
      `${icons[action]} MUTATION ${action.toUpperCase()}`,
      actionColor
    )
    console.log(`%c${message}`, `color: ${actionColor}`)
    if (context) {
      if (this.shouldUseTable(context)) {
        this.formatAsTable(context)
      } else {
        console.log(this.formatContext(context))
      }
    }
    console.groupEnd()
  }

  // 💬 Message Processing
  message(
    action: 'create' | 'update' | 'complete',
    message: string,
    context?: LogContext
  ) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.DEBUG) return

    const icons = { create: '📝', update: '🔄', complete: '✅' }

    this.logGroup(
      `${icons[action]} MESSAGE ${action.toUpperCase()}`,
      colors.message,
      true
    )
    console.log(`%c${message}`, `color: ${colors.message}`)
    if (context) console.log(this.formatContext(context))
    console.groupEnd()
  }

  // ⚠️ Warnings
  warn(message: string, context?: LogContext) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.WARN) return

    this.logGroup('⚠️ WARNING', colors.warning, true)
    console.warn(`%c${message}`, `color: ${colors.warning}`)
    if (context) {
      if (this.shouldUseTable(context)) {
        this.formatAsTable(context)
      } else {
        console.log(this.formatContext(context))
      }
    }
    console.groupEnd()
  }

  // ❌ Errors
  error(message: string, error?: Error | unknown, context?: LogContext) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.ERROR) return

    this.logGroup('❌ ERROR', colors.error, true)
    console.error(`%c${message}`, `color: ${colors.error}; font-weight: bold`)
    if (error) console.error(error)
    if (context) {
      if (this.shouldUseTable(context)) {
        this.formatAsTable(context)
      } else {
        console.log(this.formatContext(context))
      }
    }
    console.groupEnd()
  }

  // ℹ️ General Info
  info(message: string, context?: LogContext) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.INFO) return

    console.log(`%cℹ️ ${message}`, `color: ${colors.info}`)
    if (context)
      console.log(
        `%c${this.formatContext(context)}`,
        `color: ${colors.info}; opacity: 0.7`
      )
  }

  // 🎨 State Changes
  state(message: string, context?: LogContext) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.DEBUG) return

    console.log(`%c🎨 ${message}`, `color: ${colors.primary}`)
    if (context)
      console.log(
        `%c${this.formatContext(context)}`,
        `color: ${colors.primary}; opacity: 0.7`
      )
  }

  // 🚀 Performance & Timing
  perf(message: string, startTime?: number) {
    if (CURRENT_LOG_LEVEL < LOG_LEVELS.DEBUG) return

    const duration = startTime ? `(${Date.now() - startTime}ms)` : ''
    console.log(`%c🚀 ${message} ${duration}`, `color: ${colors.success}`)
  }

  // Group utilities for complex operations
  startGroup(title: string, color = colors.primary, expanded = false) {
    this.logGroup(title, color, expanded)
  }

  endGroup() {
    console.groupEnd()
  }

  // Clear console utility
  clear() {
    console.clear()
    console.log(
      `%c🧹 ChatBot Debug Console Cleared`,
      `color: ${colors.info}; font-size: 14px; font-weight: bold`
    )
  }

  // Show current configuration
  showConfig() {
    this.logGroup('⚙️ DEBUG CONFIGURATION', colors.info, true)
    console.log(`Log Level: ${Object.keys(LOG_LEVELS)[CURRENT_LOG_LEVEL]}`)
    console.log('Available Methods:', [
      'auth',
      'subscription',
      'mutation',
      'message',
      'warn',
      'error',
      'info',
      'state',
      'perf'
    ])
    console.groupEnd()
  }
}

// Export singleton instance
export const debugLogger = new ChatBotDebugLogger()

// Export types for use in components
export type { LogContext }
